
import React, { memo, useState } from 'react';
import classNames from 'classnames';

interface StyleComponentProps {
  color?: string;
  width?: string;
  height?: string;
  className?: string;
}

interface Props extends StyleComponentProps {
  checked: boolean;
  setChecked: React.Dispatch<React.SetStateAction<boolean>>;
  customCheck?: any;
  disabled?: boolean;
  name?: string;
  focusColor?: string;
  bordercolor?: string;
}

// Simple Check SVG component
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="w-full h-full text-white">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const Checkbox = memo((props: Props) => {
  const [isFocused, setIsFocused] = useState(false);
  
  const handleChange = () => {
    if (!props?.disabled) {
      props.customCheck
        ? props.customCheck()
        : props.setChecked(!props.checked);
    }
  };

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);

  return (
    <label className="relative cursor-pointer inline-flex items-center">
      {/* Hidden input for keyboard navigation */}
      <input
        type="checkbox"
        name={props.name}
        checked={props.checked}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={props.disabled}
        className="sr-only"
      />
      
      {/* Visual checkbox */}
      <div
        className={classNames(
          // Size
          props.width || 'w-6',
          props.height || 'h-6',
          // Base styles
          'rounded border-2 flex justify-center items-center transition-all duration-200',
          // Border color
          props.bordercolor || (props.color ? `border-${props.color}` : 'border-primary-main'),
          // Background color
          props.checked
            ? (props.color ? `bg-${props.color}` : 'bg-blue-600')
            : 'bg-white',
          // Disabled state
          props.disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-blue-700',
          // Focus ring
          isFocused && `ring-2 ${props.focusColor ? `ring-${props.focusColor}` : 'ring-blue-500'} ring-offset-2`,
          // Additional classes
          props.className
        )}
      >
        {props.checked && (
          <CheckIcon />
        )}
      </div>
    </label>
  );
});
